import { History, IHistoryLocation } from 'app/services/history';
import { isShallowEqual } from 'app/utils/object';
import { Subtract } from 'app/utils/types';
import React, { ComponentType, useContext } from 'react';
import { QueryProvider } from 'app/components/Contexts';
import { parse } from 'query-string';

const HistoryContext = React.createContext(new History({
  useBrowserHistory: false,
  initialUrl: '',
}));
const LocationContext = React.createContext({} as IHistoryLocation);

export const GetHistory = HistoryContext.Consumer;
export const SetHistory = ({ value, children }: { value: History, children: React.ReactNode }) => (
  <HistoryContext.Provider value={value}>
    <LocationListener history={value}>
      {children}
    </LocationListener>
  </HistoryContext.Provider>
);

export const useHistory = () => {
  return useContext(HistoryContext);
};

export const GetLocation = LocationContext.Consumer;

export const useHistoryLocation = () => {
  return useContext(LocationContext);
};

interface IProps {
  history: History;
}

interface IState {
  location: IHistoryLocation;
  query: { [key: string]: string | string[] | undefined };
}

class LocationListener extends React.Component<IProps, IState> {
  public static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
    return {
      location: nextProps.history.location,
      query: parse(nextProps.history.location.search),
    };
  }

  public state: IState = {
    location: {} as IHistoryLocation, // Will be set by getDerivedStateFromProps
    query: {},
  };

  public componentDidMount() {
    // It's possible for the location to change between the first render() and componentDidMount(),
    // so force an update to make sure we're showing the latest location.
    this.updateLocation();

    this.unlisten = this.props.history.listen(() => this.updateLocation());
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.history !== this.props.history) {
      throw new Error('Cannot change history prop passed to <LocationListener>');
    }
  }

  public componentWillUnmount() {
    this.unlisten();
  }

  public render() {
    return (
      <LocationContext.Provider value={this.state.location}>
        <QueryProvider value={this.state.query}>
          {this.props.children}
        </QueryProvider>
      </LocationContext.Provider>
    );
  }

  private updateLocation() {
    const { location } = this.props.history;

    if (!isShallowEqual(location, this.state.location)) {
      // Call `setState` so that getDerivedStateFromProps gets triggered and updates the saved
      // location, while also triggering a re-render to update the context.
      this.setState({});
    }
  }

  private unlisten = () => {};
}

interface IProvidedProps {
  history: History;
  location: IHistoryLocation;
}

/**
 * Passes a history property to the passed in component, and updates when the location changes.
 *
 * @param {React.Component} Wrap
 * @returns {React.Component}
 */
export function withHistory<T extends Partial<IProvidedProps>>(
  Wrap: ComponentType<T>,
): ComponentType<Subtract<T, Partial<IProvidedProps>>> {
  return (props) => (
    <GetHistory>{(history) =>
      <GetLocation>{(location) => {
        // TYPESCRIPT: Work around typescript limitation with generics. See
        // https://github.com/Microsoft/TypeScript/issues/28938
        const propsWithHistory = { history, location, ...props } as T;

        return <Wrap {...propsWithHistory} />;
      }}</GetLocation>
    }</GetHistory>
  );
}
